import { CustomerUserDTO } from '../../models/customer.model';
import { USER_ROLE } from '../../models/user-role.model';

export type AnzeigeStatus = 'aktiv' | 'eingeladen' | 'deaktiviert';
export type StatusFilter = AnzeigeStatus | 'alle';

export interface Angemeldet {
  id: string | null;
  rolle: string | null;
}

export interface Aktionen {
  deaktivieren: boolean;
  aktivieren: boolean;
  erneutSenden: boolean;
}

const STATUS_REIHENFOLGE: Record<AnzeigeStatus, number> = { aktiv: 0, eingeladen: 1, deaktiviert: 2 };
const KEINE_AKTION: Aktionen = { deaktivieren: false, aktivieren: false, erneutSenden: false };

/** Nur Einladungszeilen tragen ein Ablaufdatum; auch ein echter Benutzer kann auf PENDING stehen. */
export function istEinladung(p: CustomerUserDTO): boolean {
  return !!p.expiresAt;
}

export function anzeigeStatus(p: CustomerUserDTO): AnzeigeStatus {
  switch (p.status) {
    case 'PENDING':
    case 'EXPIRED':
      return 'eingeladen';
    case 'INACTIVE':
      return 'deaktiviert';
    default:
      return 'aktiv';
  }
}

export function rollenSchluessel(role: string): string {
  switch (role) {
    case USER_ROLE.PLATFORM_ADMIN:
      return 'verwaltung.benutzer.rolle.platformAdmin';
    case USER_ROLE.TENANT_ADMIN:
      return 'verwaltung.benutzer.rolle.mandantenAdmin';
    default:
      return 'verwaltung.benutzer.rolle.benutzer';
  }
}

export function avatarName(p: CustomerUserDTO): string {
  return p.displayName.replace(/^Dr\. /, '');
}

export function sortiere(personen: CustomerUserDTO[], eigeneId: string | null): CustomerUserDTO[] {
  const rang = (p: CustomerUserDTO) => (p.id === eigeneId ? -1 : STATUS_REIHENFOLGE[anzeigeStatus(p)]);
  return [...personen].sort(
    (a, b) => rang(a) - rang(b) || a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' }),
  );
}

export function zaehle(personen: CustomerUserDTO[]): { aktiv: number; deaktiviert: number } {
  const status = personen.map(anzeigeStatus);
  return {
    aktiv: status.filter((s) => s === 'aktiv').length,
    deaktiviert: status.filter((s) => s === 'deaktiviert').length,
  };
}

export function passtZuFilter(p: CustomerUserDTO, suche: string, status: StatusFilter): boolean {
  if (status !== 'alle' && anzeigeStatus(p) !== status) {
    return false;
  }
  const begriff = suche.trim().toLowerCase();
  return !begriff || `${p.displayName} ${p.email}`.toLowerCase().includes(begriff);
}

export function aktionen(p: CustomerUserDTO, angemeldet: Angemeldet): Aktionen {
  if (angemeldet.id === null) {
    return KEINE_AKTION;
  }
  if (istEinladung(p)) {
    return { ...KEINE_AKTION, erneutSenden: true };
  }
  const gesperrt =
    p.id === angemeldet.id || (p.role === USER_ROLE.PLATFORM_ADMIN && angemeldet.rolle !== USER_ROLE.PLATFORM_ADMIN);
  if (gesperrt) {
    return KEINE_AKTION;
  }
  const status = p.status ?? 'ACTIVE';
  return { deaktivieren: status === 'ACTIVE', aktivieren: status === 'INACTIVE', erneutSenden: false };
}

export type RollenSperre = 'eigene' | 'platformAdmin' | 'einladung';

export function rollenSperre(p: CustomerUserDTO, angemeldet: Angemeldet): RollenSperre | null {
  if (p.id === angemeldet.id) {
    return 'eigene';
  }
  if (istEinladung(p) || p.status === 'PENDING') {
    return 'einladung';
  }
  return p.role === USER_ROLE.PLATFORM_ADMIN ? 'platformAdmin' : null;
}
